/**
 * 认证API模块
 * 提供用户注册、登录、登出等功能
 */

// 防止重复声明 - 只在实例不存在时创建
if (window.authAPI) {
  console.warn('authAPI already exists, skipping redeclaration');
} else {
  
class AuthAPI {
  constructor() {
    this.baseURL = window.API_BASE_URL;
    this.ACCESS_TOKEN_KEY = 'accessToken';
    this.REFRESH_TOKEN_KEY = 'refreshToken';

    // 统一的模板类型常量
    this.TEMPLATES = {
      REGISTER: 1, // 注册
      FORGET_PASSWORD: 2, // 忘记密码
      RESET_PASSWORD: 3, //  登陆态下忘记密码/三方添加密码
    };
  }

  /**
   * 邮箱注册
   * @param {Object} registerData - 注册数据
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async register(registerData) {
    const requestData = {
      email: registerData.email || registerData.userEmail, // 兼容旧格式
      firstName: registerData.firstName,
      lastName: registerData.lastName,
      password: registerData.password,
      enableNotification: 2,
      country: registerData.country || null, // 国家/地区英文名称，如 "United States"（可选）
      purchasePurpose: registerData.expectPrice || null,
    };

    const response = await fetch(`${this.baseURL}/authority/account/register`, {
      method: 'POST',
      headers: {
        'X-Client-Type': 'web',
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    }).catch(error => null);

    if (!response) {
      return { code: 0, message: 'Network error, please check your connection' };
    }

    const result = await response.json().catch(() => null);

    if (!result) {
      return { code: 0, message: 'Invalid response format' };
    }

    // 直接返回后端原始响应对象
    return result;
  }

  /**
   * Generate a best-effort browser device identifier without account data.
   */
  getDeviceId() {
    if (this.deviceId) return this.deviceId;

    // Best-effort browser fingerprint; never use it as proof of identity.
    let canvasSignature = '';
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 240;
      canvas.height = 40;
      const context = canvas.getContext('2d');
      if (context) {
        context.font = '16px Arial';
        context.fillStyle = '#1769aa';
        context.fillText('Shop browser device 0123456789', 4, 24);
        canvasSignature = canvas.toDataURL();
      }
    } catch (error) {
      // Privacy settings may block canvas reads; other signals still work.
    }

    const signals = JSON.stringify([
      navigator.userAgent || '',
      navigator.language || '',
      navigator.platform || '',
      navigator.hardwareConcurrency || 0,
      navigator.maxTouchPoints || 0,
      canvasSignature
    ]);
    // Non-cryptographic hash: this identifier is only an auxiliary device signal.
    const seeds = [2166136261, 2246822507, 3266489909, 668265263];
    this.deviceId = 'browser-v1-' + seeds.map(seed => {
      let hash = seed;
      for (let index = 0; index < signals.length; index++) {
        hash = Math.imul(hash ^ signals.charCodeAt(index), 16777619);
      }
      return (hash >>> 0).toString(16).padStart(8, '0');
    }).join('');
    return this.deviceId;
  }

  /**
   * 邮箱登录
   * @param {string} email - 邮箱
   * @param {string} password - 密码
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async login(email, password) {
    const requestData = {
      email: email,
      password: password,
      deviceId: this.getDeviceId()
    };

    const response = await fetch(`${this.baseURL}/authority/account/login`, {
      method: 'POST',
      headers: {
        'X-Client-Type': 'web',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData)
    }).catch(error => null);

    if (!response) {
      return { code: 0, message: 'Network error, please check your connection' };
    }

    const result = await response.json().catch(() => null);

    if (!result) {
      return { code: 0, message: 'Invalid response format' };
    }

    // 成功时，保存新 token（兼容新旧两种结构）
    // 新结构: result.data.token.accessToken / result.data.token.refreshToken
    // 旧结构: result.data.accessToken / result.data.refreshToken
    if (result.code === 200 && result.data) {
      const loginData = result.data;
      const tokenData = loginData.token || loginData;
      const accessToken = tokenData.accessToken || '';
      const refreshToken = tokenData.refreshToken || '';

      if (accessToken) {
        // 保存新的 token（旧 token 已在登录开始时清除）
        this.saveAccessToken(accessToken);
        // 兼容旧代码：也保存 accessToken 到 authToken
        this.saveAuthToken(accessToken);
      }

      if (refreshToken) {
        this.saveRefreshToken(refreshToken);
      }

      // 登录接口约定：只要 code=200 就允许进入下一步，统一规范成功态
      // 这样调用方不会再被非 "success" 的 message 阻塞。
      result.message = 'success';
    }

    // 直接返回后端原始响应对象
    return result;
  }

  /**
   * 保存 accessToken 到 cookie
   * @param {string} token - accessToken
   */
  saveAccessToken(token) {
    this._saveToCookie(this.ACCESS_TOKEN_KEY, token, 7);
  }

  /**
   * 保存 refreshToken 到 cookie
   * @param {string} token - refreshToken
   */
  saveRefreshToken(token) {
    this._saveToCookie(this.REFRESH_TOKEN_KEY, token, 30); // refreshToken 有效期更长，30天
  }

  /**
   * 保存 authToken 到 cookie (兼容旧代码)
   * @param {string} token - authToken
   */
  saveAuthToken(token) {
    this._saveToCookie(this.ACCESS_TOKEN_KEY, token, 7);
  }

  /**
   * 通用的 cookie 保存方法
   * @param {string} key - cookie 键名
   * @param {string} value - cookie 值
   * @param {number} days - 有效期天数
   */
  _saveToCookie(key, value, days) {
    try {
      const expires = new Date();
      expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));

      const isSecure = window.location.protocol === 'https:';
      let cookieString = `${key}=${value}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;

      if (isSecure) {
        cookieString += '; Secure';
      }

      document.cookie = cookieString;
    } catch (error) {
      // 静默失败
    }
  }

  /**
   * 获取 accessToken
   * @returns {string} accessToken
   */
  getAccessToken() {
    return this._getFromCookie(this.ACCESS_TOKEN_KEY);
  }

  /**
   * 获取 refreshToken
   * @returns {string} refreshToken
   */
  getRefreshToken() {
    return this._getFromCookie(this.REFRESH_TOKEN_KEY);
  }

  /**
   * 兼容旧方法名：获取 authToken（等同于 accessToken）
   * @returns {string} token
   */
  getAuthToken() {
    return this.getAccessToken();
  }

  /**
   * 通用的 cookie 获取方法
   * @param {string} key - cookie 键名
   * @returns {string} cookie 值
   */
  _getFromCookie(key) {
    try {
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === key) {
          return value || '';
        }
      }
      return '';
    } catch (error) {
      return '';
    }
  }

  /**
   * 清除所有认证token（accessToken, refreshToken）
   */
  clearAllTokens() {
    this._clearCookie(this.ACCESS_TOKEN_KEY);
    this._clearCookie(this.REFRESH_TOKEN_KEY);
  }

  /**
   * 清除认证token（兼容旧方法名）
   */
  clearAuthToken() {
    this.clearAllTokens();
    
    // 清除所有相关的 sessionStorage 状态
    try {
      sessionStorage.removeItem('fromProductPage');
      sessionStorage.removeItem('productPageUrl');
      sessionStorage.removeItem('postLoginCheckoutUrl');
      sessionStorage.removeItem('productVariantId');
      sessionStorage.removeItem('productQuantity');
      sessionStorage.removeItem('productId');
      sessionStorage.removeItem('productCountry');
      sessionStorage.removeItem('productInvitationCode');
      sessionStorage.removeItem('loginEmail');
      sessionStorage.removeItem('verifyEmail');
      sessionStorage.removeItem('passwordMode');
      sessionStorage.removeItem('verifyCode');
      sessionStorage.removeItem('resetToken');
      sessionStorage.removeItem('customerId');
      sessionStorage.removeItem('verificationType');
      sessionStorage.removeItem('activateEmail');
      sessionStorage.removeItem('activeUrl');
      sessionStorage.removeItem('order_history_cache');
      // 所有按用户拆分的订单缓存
      Object.keys(sessionStorage).forEach(key => {
        if (key.indexOf('order_history_cache_') === 0 || key.startsWith('points-coupons:v1:')) {
          sessionStorage.removeItem(key);
        }
      });
      sessionStorage.removeItem('userProfileData');
      sessionStorage.removeItem('userProfileTimestamp');
      sessionStorage.removeItem('passwordMode'); // 再保底清一遍
      sessionStorage.removeItem('registerCredentials');
    } catch (error) {
      // 静默失败
    }
  }

  /**
   * 通用的 cookie 清除方法
   * @param {string} key - cookie 键名
   */
  _clearCookie(key) {
    try {
      const isSecure = window.location.protocol === 'https:';
      let cookieString = `${key}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax`;

      if (isSecure) {
        cookieString += '; Secure';
      }

      document.cookie = cookieString;
    } catch (error) {
      // 静默失败
    }
  }

  /**
   * 检查是否已登录
   * @returns {boolean} 是否已登录
   */
  isLoggedIn() {
    const accessToken = this.getAccessToken();
    return accessToken && accessToken.length > 0;
  }

  /**
   * 严格登录校验：需有token且profile请求成功
   * @returns {Promise<boolean>}
   */
  async isLoggedInStrict() {
    const accessToken = this.getAccessToken();
    if (!accessToken) return false;
    const isValid = await this.validateToken();
    return !!isValid;
  }

  /**
   * 登出
   */
  async logout() {
    const accessToken = this.getAccessToken();

    if (accessToken) {
      // 调用后端登出接口
      await fetch(`${this.baseURL}/authority/account/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
          'X-Client-Type': 'web',
          'Refresh-token': this.getRefreshToken ? this.getRefreshToken() : ''
        }
      }).catch(() => {
        // 忽略错误，继续清除本地token
      });
    }

    // 无论API调用是否成功，都清除本地所有token和相关 sessionStorage 状态
    this.clearAuthToken();
  }

  /**
   * 验证token有效性
   * @returns {Promise<boolean>} token是否有效
   */
  async validateToken() {
    const validationId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const log = (level, stage, details = {}) => {
      console[level]('[AuthAPI.validateToken]', {
        validationId,
        stage,
        timestamp: new Date().toISOString(),
        pathname: window.location.pathname,
        ...details
      });
    };
    try {
      const accessToken = this.getAccessToken();
      const refreshToken = this.getRefreshToken();
      log('info', 'start', {
        hasAccessToken: !!accessToken,
        hasRefreshToken: !!refreshToken
      });
      if (!accessToken) {
        log('warn', 'missing-access-token', { valid: false });
        return false;
      }
      const response = await fetch(`${this.baseURL}/authority/account/profile`, {
        method: 'GET',
        headers: {
          'X-Client-Type': 'web',
          'Authorization': `Bearer ${accessToken}`,
          'Refresh-token': refreshToken || ''
        }
      }).catch(() => {
        log('warn', 'request-failed', { valid: false });
        return null;
      });
      if (!response) return false;
      log('info', 'response', { httpStatus: response.status });
      const result = await response.json().catch(() => {
        log('warn', 'response-parse-failed', { valid: false });
        return null;
      });
      if (!result) {
        log('warn', 'empty-result', { valid: false });
        return false;
      }
      const valid = result.code === 200;
      log(valid ? 'info' : 'warn', 'result', {
        businessCode: result.code,
        valid,
        hasAccessTokenAfterRequest: !!this.getAccessToken(),
        hasRefreshTokenAfterRequest: !!this.getRefreshToken()
      });
      return valid;
    } catch (error) {
      log('warn', 'unexpected-error', { valid: false });
      return false;
    }
  }

  /**
   * 自动登录检查
   * @returns {Promise<boolean>} 是否自动登录成功
   */
  async autoLogin() {
    try {
      if (!this.isLoggedIn()) {
        return false;
      }

      const isValid = await this.validateToken();
      if (!isValid) {
        await this.logout();
        return false;
      }

      return true;
    } catch (error) {
      console.error('Auto login failed:', error);
      await this.logout();
      return false;
    }
  }

  /**
   * 检查cookie是否正常工作
   * @returns {boolean} cookie是否可用
   */
  isCookieEnabled() {
    try {
      // 测试cookie是否可用
      const testKey = 'test_cookie_' + Date.now();
      const testValue = 'test_value';

      // 设置测试cookie
      document.cookie = `${testKey}=${testValue}; path=/`;

      // 检查是否设置成功
      const cookies = document.cookie.split(';');
      for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === testKey && value === testValue) {
          // 清除测试cookie
          document.cookie = `${testKey}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
          return true;
        }
      }

      return false;
    } catch (error) {
      console.error('Cookie test failed:', error);
      return false;
    }
  }

  /**
   * 获取token存储方式信息
   * @returns {Object} 存储方式信息
   */
  getTokenStorageInfo() {
    const cookieToken = this.getAuthToken();

    return {
      cookieEnabled: this.isCookieEnabled(),
      cookieToken: cookieToken ? 'exists' : 'not found',
      primarySource: cookieToken ? 'cookie' : 'none'
    };
  }

  /**
   * 发送验证码
   * @param {string} receiver - 接收者邮箱
   * @param {number} templateCode - 模板代码
   * @returns {Promise<{success: boolean, data?: Object, error?: string, message?: string}>} 发送结果
   */
  async sendVerifyCode(receiver, templateCode) {
    const requestData = {
      scene: templateCode,
      email: receiver
    };

    // 获取 token（如果有）
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken();

    // 构建 headers
    const headers = {
      'X-Client-Type': 'app',
      'Content-Type': 'application/json',
    };

    // 如果有 token，添加到 headers
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    if (refreshToken) {
      headers['Refresh-token'] = refreshToken;
    }

    const response = await fetch(`${this.baseURL}/authority/account/verify-code/send`, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(requestData)
    }).catch(error => null);

    if (!response) {
      return { code: 0, message: 'Network error, please check your connection' };
    }

    const result = await response.json().catch(() => null);

    if (!result) {
      return { code: 0, message: 'Invalid response format' };
    }

    // 直接返回后端原始响应对象
    return result;
  }

  /**
   * 验证邮箱（兼容旧方法）
   * @param {string} email - 邮箱地址
   * @param {number} type - 类型：1=注册(REGISTER)，2=修改(MODIFY)
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async verifyEmail(email, type = 1) {
    const requestData = {
      email: email,
      type: type
    };

    const response = await fetch(`${this.baseURL}/user/verify_email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestData)
    }).catch(error => null);

    if (!response) {
      return { code: 0, message: 'Network error, please check your connection' };
    }

    const result = await response.json().catch(() => null);

    if (!result) {
      return { code: 0, message: 'Invalid response format' };
    }

    // 直接返回后端原始响应对象
    return result;
  }

  /**
   * 验证验证码
   * @param {string} email - 邮箱地址
   * @param {string} code - 验证码
   * @param {number} templateCode - 模板代码
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async verifyCode(email, code, templateCode) {
    const requestData = {
      scene: templateCode,
      email: email,
      code: code
    };

    const response = await fetch(`${this.baseURL}/authority/account/verify-code/validate`, {
      method: 'POST',
      headers: {
        'X-Client-Type': 'web',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestData)
    }).catch(error => null);

    if (!response) {
      return { code: 0, message: 'Network error, please check your connection' };
    }

    const result = await response.json().catch(() => null);

    if (!result) {
      return { code: 0, message: 'Invalid response format' };
    }

    // 直接返回后端原始响应对象
    return result;
  }


  /**
   * 登录态修改密码
   * @param {string} verifyCode - 验证码
   * @param {string} password - 新密码
   * @returns {Promise<Object>} 修改结果
   */
  async changePasswordForChange(verifyCode, password) {
    return this.changePassword(this.TEMPLATES.CHANGE_PASSWORD, verifyCode, password);
  }

  /**
   * 已登录修改密码（独立接口）
   * @param {string} newPassword - 新密码
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async changePasswordLoggedIn(newPassword) {
    const response = await fetch(`${this.baseURL}/authority/account/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAccessToken()}`,
        'X-Client-Type': 'web',
        'Refresh-token': this.getRefreshToken ? this.getRefreshToken() : ''
      },
      body: JSON.stringify({ newPassword: newPassword })
    }).catch(() => null);
    if (!response) {
      return { code: 0, message: 'Network error, please check your connection' };
    }
    const result = await response.json().catch(() => null);
    if (!result) {
      return { code: 0, message: 'Invalid response format' };
    }
    try {
      if (result.code === 200 && result.data) {
        if (result.data.accessToken) {
          this.saveAccessToken(result.data.accessToken);
          this.saveAuthToken(result.data.accessToken);
        }
        if (result.data.refreshToken) {
          this.saveRefreshToken(result.data.refreshToken);
        }
        try {
          if (result.data.tokenType) {
            sessionStorage.setItem('tokenType', result.data.tokenType);
          }
          if (typeof result.data.expiresIn === 'number') {
            sessionStorage.setItem('accessTokenExpiresAt', String(Date.now() + result.data.expiresIn * 1000));
          }
          if (typeof result.data.refreshExpiresIn === 'number') {
            sessionStorage.setItem('refreshTokenExpiresAt', String(Date.now() + result.data.refreshExpiresIn * 1000));
          }
          if (result.data.jti) {
            sessionStorage.setItem('tokenJti', result.data.jti);
          }
        } catch (e) {}
      }
    } catch (e) {
    }
    return result;
  }

  /**
   * 游客模式/忘记密码重置密码（使用临时令牌）
   * @param {Object} payload
   * @param {string} payload.password - 新密码
   * @param {string} payload.mail - 邮箱
   * @param {string} payload.tempToken - 验证接口返回的临时令牌
   * @param {number} [payload.mailSceneEnum=0] - 场景枚举，默认2（忘记密码）
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async resetPasswordWithTempToken(payload) {
    let scene = payload && payload.mailSceneEnum != null ? payload.mailSceneEnum : null;
    if (scene == null) {
      try {
        const vt = sessionStorage.getItem('verificationType');
        if (vt === 'activate') {
          scene = 7;
        } else if (vt === 'reset-password') {
          scene = 2;
        } else {
          scene = 2;
        }
      } catch (e) {
        scene = 2;
      }
    }
    const requestData = {
      password: payload.password,
      mail: payload.mail,
      tempToken: payload.tempToken,
      mailSceneEnum: scene
    };
    const response = await fetch(`${this.baseURL}/authority/account/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Type': 'web'
      },
      body: JSON.stringify(requestData)
    }).catch(() => null);
    if (!response) {
      return { code: 0, message: 'Network error, please check your connection' };
    }
    const result = await response.json().catch(() => null);
    if (!result) {
      return { code: 0, message: 'Invalid response format' };
    }
    return result;
  }

  /**
   * 验证码方式重置密码
   * @param {Object} payload - 重置密码参数
   * @param {string} payload.email - 邮箱地址
   * @param {string} payload.verifyCode - 验证码
   * @param {string} payload.newPassword - 新密码
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async resetPasswordWithVerifyCode(payload) {
    const requestData = {
      email: payload.email,
      newPassword: payload.newPassword
    };

    const response = await fetch(`${this.baseURL}/authority/account/reset-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Type': 'web'
      },
      body: JSON.stringify(requestData)
    }).catch(() => null);

    if (!response) {
      return { code: 0, message: 'Network error, please check your connection' };
    }

    const result = await response.json().catch(() => null);

    if (!result) {
      return { code: 0, message: 'Invalid response format' };
    }

    return result;
  }


  /**
   * 更新用户信息接口
   * @param {Object} userData - 用户数据
   * @param {string} userData.firstName - 名字
   * @param {string} userData.lastName - 姓氏
   * @param {string} userData.email - 邮箱
   * @param {number} userData.enableNotification - 通知设置
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async updateUserInfo(userData) {
    const requestData = {
      firstName: userData.firstName || null,
      lastName: userData.lastName || null,
      email: userData.email || null,
      userPhone: userData.userPhone || null,
      enableNotification: userData.enableNotification || null,
      password:userData.password || null,
    };
 
    const response = await fetch(`${this.baseURL}/authority/account/change-profile`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.getAccessToken()}`,
        'X-Client-Type': 'web',
        'Refresh-token': this.getRefreshToken ? this.getRefreshToken() : ''
      },
      body: JSON.stringify(requestData)
    }).catch(error => null);
    if (!response) {
      return { code: 0, message: 'Network error, please check your connection' };
    }
    
    // 解析响应 JSON
    const result = await response.json().catch(() => null);
    
    
    if (!result) {
      return { code: 0, message: 'Invalid response format' };
    }

    // 直接返回后端原始响应对象
    return result;
  }

  /**
   * 激活账户接口
   * @param {Object} payload - 激活账户参数
   * @param {string} payload.email - 邮箱地址
   * @param {string} payload.password - 密码
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async activateAccount(payload) {
    const requestData = {
      email: payload.email,
      password: payload.password
    };

    const response = await fetch(`${this.baseURL}/authority/account/activate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Type': 'web'
      },
      body: JSON.stringify(requestData)
    }).catch(() => null);

    if (!response) {
      return { code: 0, message: 'Network error, please check your connection' };
    }

    const result = await response.json().catch(() => null);

    if (!result) {
      return { code: 0, message: 'Invalid response format' };
    }

    return result;
  }


  /**
   * 注销账户（需要 accessToken 与 refreshToken）
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async deactivateAccount() {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken ? this.getRefreshToken() : '';

    if (!accessToken) {
      return { code: 0, message: 'Not logged in' };
    }

    const response = await fetch(`${this.baseURL}/authority/account/deactivate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'X-Client-Type': 'web',
        'Refresh-token': refreshToken,
      }
    }).catch(error => null);

    if (!response) {
      return { code: 0, message: 'Network error, please check your connection' };
    }

    const result = await response.json().catch(() => null);

    if (!result) {
      return { code: 0, message: 'Invalid response format' };
    }

    // 注销成功后清除本地 token
    if (result.code === 200) {
      this.clearAllTokens();
    }

    // 直接返回后端原始响应对象
    return result;
  }

  /**
   * Shopify Rest 接口 - 用于找回密码流程 已废弃
   * @param {string} email - 邮箱地址
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async shopifyRest(email) {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken ? this.getRefreshToken() : '';

    const requestData = {
      receiver: email
    };

    const headers = {
      'Content-Type': 'application/json',
      'X-Client-Type': 'web'
    };

    // 如果有 token，添加到 headers
    if (accessToken) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }
    if (refreshToken) {
      headers['Refresh-token'] = refreshToken;
    }

    const response = await fetch(`${this.baseURL}/user/api/shopify/shopifyRest`, {
      method: 'PUT',
      headers: headers,
      body: JSON.stringify(requestData)
    }).catch(error => null);

    if (!response) {
      return { code: 0, message: 'Network error, please check your connection' };
    }

    const result = await response.json().catch(() => null);

    if (!result) {
      return { code: 0, message: 'Invalid response format' };
    }

    // 直接返回后端原始响应对象
    return result;
  }


  /**
   * Reset 接口 - 统一的重置密码接口
   * 支持两种调用方式：
   * 1. 邮件链接方式：reset({ customerId, resetToken, password })
   * 2. 验证码方式：reset({ receiver, password, mailScene })
   * 
   * 注意：修改密码请使用 updateUserInfo 接口，此接口仅用于忘记密码和添加密码场景
   * 
   * @param {Object} options - 重置密码参数
   * @param {string} [options.customerId] - 客户ID（邮件链接方式）
   * @param {string} [options.resetToken] - 重置令牌（邮件链接方式）
   * @param {string} [options.receiver] - 邮箱地址（验证码方式）
   * @param {number} [options.mailScene] - 邮件场景（验证码方式，2=忘记密码, 3=添加密码）
   * @param {string} options.password - 新密码（必需）
   * @returns {Promise<Object>} 返回后端原始响应对象
   * 
   * @example
   * // 邮件链接方式
   * await authAPI.reset({ customerId: '123', resetToken: 'abc', password: 'newPass123' });
   * 
   * // 验证码方式（忘记密码）
   * await authAPI.reset({ receiver: 'user@example.com', password: 'newPass123', mailScene: 2 });
   * 
   * // 验证码方式（添加密码）
   * await authAPI.reset({ receiver: 'user@example.com', password: 'newPass123', mailScene: 3 });
   */
  async reset(options) {
    const requestData = {};
    
    // 场景1：使用 customerId + resetToken + password（邮件链接）
    if (options.customerId && options.resetToken) {
      requestData.customerId = options.customerId;
      requestData.resetToken = options.resetToken;
      requestData.password = options.password;
    }
    // 场景2：使用 receiver + password + mailScene（验证码）
    else if (options.receiver) {
      requestData.receiver = options.receiver;
      requestData.password = options.password;
      // mailScene: 2=忘记密码, 3=添加密码（不再支持修改密码，修改密码请使用 updateUserInfo）
      requestData.mailScene = options.mailScene || 2;
    }
    // 参数错误
    else {
      return { 
        code: 0, 
        message: 'Invalid parameters: either (customerId + resetToken) or (receiver) is required' 
      };
    }

    const response = await fetch(`${this.baseURL}/user/api/app/reset`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Client-Type': 'web',
      },
      body: JSON.stringify(requestData)
    }).catch(error => null);

    if (!response) {
      return { code: 0, message: 'Network error, please check your connection' };
    }

    const result = await response.json().catch(() => null);

    if (!result) {
      return { code: 0, message: 'Invalid response format' };
    }

    // 直接返回后端原始响应对象
    return result;
  }

  /**
   * 修改邮箱地址
   * @param {Object} payload - 修改邮箱参数
   * @param {string} payload.newEmail - 新邮箱地址
   * @param {string} payload.verifyCode - 验证码
   * @returns {Promise<Object>} 返回后端原始响应对象
   */
  async changeEmail(payload) {
    const accessToken = this.getAccessToken();
    const refreshToken = this.getRefreshToken ? this.getRefreshToken() : '';

    if (!accessToken) {
      return { code: 0, message: 'Not logged in' };
    }

    const requestData = {
      newEmail: payload.newEmail,
      verifyCode: payload.verifyCode
    };

    const response = await fetch(`${this.baseURL}/authority/account/change-email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
        'X-Client-Type': 'web',
        'Refresh-token': refreshToken,
      },
      body: JSON.stringify(requestData)
    }).catch(error => null);

    if (!response) {
      return { code: 0, message: 'Network error, please check your connection' };
    }

    const result = await response.json().catch(() => null);

    if (!result) {
      return { code: 0, message: 'Invalid response format' };
    }

    // 修改邮箱成功后，后端会下发新 token，需立即覆盖本地 token
    // 这样后续回到 profile 的请求会使用最新会话
    try {
      if (result.code === 200 && result.data) {
        if (result.data.accessToken) {
          this.saveAccessToken(result.data.accessToken);
          this.saveAuthToken(result.data.accessToken);
        }
        if (result.data.refreshToken) {
          this.saveRefreshToken(result.data.refreshToken);
        }
        if (result.data.tokenType) {
          sessionStorage.setItem('tokenType', result.data.tokenType);
        }
        if (typeof result.data.expiresIn === 'number') {
          sessionStorage.setItem('accessTokenExpiresAt', String(Date.now() + result.data.expiresIn * 1000));
        }
        if (typeof result.data.refreshExpiresIn === 'number') {
          sessionStorage.setItem('refreshTokenExpiresAt', String(Date.now() + result.data.refreshExpiresIn * 1000));
        }
        if (result.data.jti) {
          sessionStorage.setItem('tokenJti', result.data.jti);
        }
      }
    } catch (e) {}

    // 直接返回后端原始响应对象
    return result;
  }
}

// 创建全局实例
window.authAPI = new AuthAPI();

// 导出模块（如果使用模块系统）
if (typeof module !== 'undefined' && module.exports) {
  module.exports = AuthAPI;
}

} // 结束 AuthAPI 重复声明保护 

// Import a complete token pair before page scripts start authenticated requests.
(() => {
  // auth-api is loaded in the head, before the rewards markup exists.
  const isPointsPage = /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?pages\/(?:points-activity|points-redemption|points-coupons|rewards|rewards-desktop)\/?$/i.test(window.location.pathname);
  const url = new URL(window.location.href);
  const fragment = window.consumeStoreAuthHandoff?.() || new URLSearchParams(url.hash.slice(1));
  const isHandoff = fragment.get('_points_auth') === '1' || fragment.get('_store_auth') === '1';
  if (!isHandoff && !isPointsPage) return;
  const params = isHandoff ? fragment : url.searchParams;
  const readToken = (name) => {
    const values = [...params.getAll(name), ...params.getAll(name.toUpperCase())];
    return values.length === 1 && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(values[0]) ? values[0] : '';
  };
  const at = readToken('at');
  const rt = readToken('rt');
  // Scrub fragment credentials before any later scripts or API requests run.
  if (isHandoff) {
    url.hash = fragment.get('anchor') || '';
    history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  }
  if (!at || !rt) return;
  window.authAPI.saveAccessToken(at);
  window.authAPI.saveRefreshToken(rt);
  window.authAPI.saveAuthToken(at);
  if (window.authAPI.getAccessToken() !== at || window.authAPI.getRefreshToken() !== rt) return;
  for (const name of ['at', 'rt', 'AT', 'RT']) url.searchParams.delete(name);
  history.replaceState(history.state, '', url.pathname + url.search + url.hash);
  try {
    sessionStorage.removeItem('userProfileData');
    sessionStorage.removeItem('userProfileTimestamp');
  } catch (_) { /* Storage may be disabled; cookie authentication still works. */ }
  window.userProfileAPI?.clearCachedUserData?.();
})();
